/**
 * Design system for AI-generated SVG visualizations.
 *
 * Single source of truth shared by the LLM prompt (src/node) and the renderer (src/core):
 * the prompt text and the CSS custom properties are both generated from DIAGRAM_TOKENS,
 * so a token cannot exist in one without the other.
 *
 * Theme *paths* are stored rather than values because src/shared must not import from the
 * React package; the client resolves them against the live theme.
 */

const DIAGRAM_TOKEN_PREFIX = '--kd-';

// 1 user unit == 1 CSS pixel at the conversation's render width, which is what makes the
// font sizes in the prompt contract meaningful.
const DIAGRAM_VIEWBOX_WIDTH = 720;
const DIAGRAM_VIEWBOX_HEIGHT = 405;
const DIAGRAM_MARGIN = 24;

const DIAGRAM_TOKENS = [
  { name: '--kd-ink', themePath: 'colors.body', use: 'text, edges, arrows and borders on the transparent canvas' },
  { name: '--kd-ink-muted', themePath: 'colors.muted', use: 'secondary labels, axis ticks, captions, annotations' },
  { name: '--kd-surface', themePath: 'colors.cardBackground', use: 'fill of a node, box, card or bar' },
  {
    name: '--kd-surface-alt',
    themePath: 'backgrounds.tableHeader',
    use: 'banded rows, group containers, legend chips'
  },
  { name: '--kd-border', themePath: 'colors.cardBorder', use: 'stroke around anything filled with a surface color' },
  { name: '--kd-grid', themePath: 'colors.chart.gridLine', use: 'gridlines, axis lines, ticks, alignment guides' },
  { name: '--kd-primary', themePath: 'colors.primary', use: 'highlight, selection, the subject of the answer' },
  { name: '--kd-success', themePath: 'colors.success', use: 'healthy, up, within threshold, cleared' },
  { name: '--kd-warning', themePath: 'colors.warning', use: 'degraded, approaching threshold' },
  { name: '--kd-danger', themePath: 'colors.danger', use: 'down, failing, over threshold' },
  { name: '--kd-critical', themePath: 'colors.severity.critical', use: 'alert severity "critical" only' }
];

// Categorical palette for data viz. Sourced from the theme's neutral categorical ramp
// (distinct light/dark variants) rather than colors.pie, which has no dark variant.
const DIAGRAM_SERIES_COUNT = 8;
const DIAGRAM_SERIES_THEME_PATH = 'colors.stateTimeline.categoricalNeutral';

// Ordered low -> high ramp, reusing the product's own severity scale so a generated
// utilization visual is colored the same way the real capacity widgets are.
const DIAGRAM_HEAT_THEME_PATHS = [
  'colors.severity.clear',
  'colors.severity.minor',
  'colors.severity.warning',
  'colors.severity.major',
  'colors.severity.critical'
];

function seriesTokenName(index) {
  return `${DIAGRAM_TOKEN_PREFIX}series-${index}`;
}

function heatTokenName(index) {
  return `${DIAGRAM_TOKEN_PREFIX}heat-${index}`;
}

/** Every legal --kd-* name, with the indexed families expanded. */
const DIAGRAM_TOKEN_NAMES = [
  ...DIAGRAM_TOKENS.map((token) => token.name),
  ...Array.from({ length: DIAGRAM_SERIES_COUNT }, (_, i) => seriesTokenName(i + 1)),
  ...DIAGRAM_HEAT_THEME_PATHS.map((_, i) => heatTokenName(i + 1))
];

/**
 * Resolve every token to a concrete value for the supplied theme.
 * `get` is injected so this module stays free of a lodash dependency.
 *
 * @returns {Array<{ name: string, value: string }>} tokens that resolved; unresolved are dropped
 */
function resolveDiagramTokens(theme, get) {
  const resolved = [];

  const push = (name, value) => {
    if (typeof value === 'string' && value.length > 0) {
      resolved.push({ name, value });
    }
  };

  DIAGRAM_TOKENS.forEach(({ name, themePath }) => push(name, get(theme, themePath)));

  const series = get(theme, DIAGRAM_SERIES_THEME_PATH) || [];
  for (let i = 0; i < DIAGRAM_SERIES_COUNT; i += 1) {
    push(seriesTokenName(i + 1), series[i]);
  }

  DIAGRAM_HEAT_THEME_PATHS.forEach((themePath, i) => push(heatTokenName(i + 1), get(theme, themePath)));

  return resolved;
}

function buildPaletteReference() {
  const lines = DIAGRAM_TOKENS.map(({ name, use }) => `${name} - ${use}`);
  lines.push(
    `${DIAGRAM_TOKEN_PREFIX}series-1..${DIAGRAM_SERIES_COUNT} - one per category in a chart, sankey or legend`
  );
  lines.push(
    `${DIAGRAM_TOKEN_PREFIX}heat-1..${DIAGRAM_HEAT_THEME_PATHS.length} - ordered low to high ramp for utilization or saturation`
  );
  return lines.join('\n');
}

/**
 * The design-system section of the LLM system prompt, generated from the token list above.
 */
function buildSvgDesignSystemPrompt() {
  return `Color every element with the Kentik diagram palette below, never with hardcoded hex values.
Each color MUST be a var() WITH a fallback, e.g. fill="var(--kd-surface, transparent)" or stroke="var(--kd-ink, currentColor)".
A var() with no fallback renders BLACK and is invisible in dark mode, so NEVER write a bare reference like var(--kd-surface).
Do not set font-family; it is inherited. Reserve raw hex for third-party brand colors only.

Palette:
${buildPaletteReference()}

Severity maps as: critical to --kd-critical, severe and major to --kd-danger, warning and minor to --kd-warning, notice to --kd-ink-muted, clear to --kd-success.
Emphasize with stroke="var(--kd-primary, currentColor)" and stroke-width="2.5". De-emphasize with opacity="0.35", never by choosing a lighter color.
To encode a neutral quantity (e.g. traffic volume) keep one color and vary fill-opacity across 0.2, 0.4, 0.6, 0.8, 1.

Layout rules, in user units where 1 unit renders as 1 pixel:
Always viewBox="0 0 ${DIAGRAM_VIEWBOX_WIDTH} H" with no width or height attributes; H defaults to ${DIAGRAM_VIEWBOX_HEIGHT}. Keep all content inside a ${DIAGRAM_MARGIN} unit margin.
Font size: title 20 weight 600, section heading 15 weight 600, node and data labels 13, axis ticks and legend 12. Never below 12.
Text does not wrap: emit one <tspan x="..." dy="1.25em"> per line. Size a box as width = 0.6 * fontSize * labelLength + 24, height at least 32.
Boxes use rx="6". Edges use stroke-width="1.5", stroke-linecap="round", fill="none". stroke-dasharray="4 4" means logical, inferred or backup.
For arrowheads define one <marker> in <defs> and reference it with marker-end="url(#arrow)", giving the marker path the same color as its edge.
Add a legend only when two or more categories are encoded: a 12x12 rx="2" swatch, its label at +18, entries about 110 apart, on a row at y = H - 16.`;
}

module.exports = {
  DIAGRAM_TOKEN_PREFIX,
  DIAGRAM_TOKENS,
  DIAGRAM_TOKEN_NAMES,
  DIAGRAM_SERIES_COUNT,
  DIAGRAM_SERIES_THEME_PATH,
  DIAGRAM_HEAT_THEME_PATHS,
  DIAGRAM_VIEWBOX_WIDTH,
  DIAGRAM_VIEWBOX_HEIGHT,
  DIAGRAM_MARGIN,
  seriesTokenName,
  heatTokenName,
  resolveDiagramTokens,
  buildSvgDesignSystemPrompt
};
